import React, { useContext, useEffect, useRef, useState } from "react";
import Input from "components/Elements/Input";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Select from "react-select";
import DataTable from "datatables.net-react";
import DT from "datatables.net-dt";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import Swal from "sweetalert2";
import {
  getLiveEvent,
  saveLiveEvent,
  deleteLiveEvent,
} from "../../services/liveevents";
import { AppContext } from "../../contexts/AppContext";
import {
  getAbbreviatedTimeZone,
  getFullTimeZone,
  fromDateToString,
  dateTimeFormatter,
  getLocalDateFromString,
} from "../../utils/utils";
import { getInferenceWorkflows } from "../../services/workflows";
import { getAgents } from "../../services/clients";

DataTable.use(DT);

const eventTypeOptions = [
  { label: "Live", value: "live" },
  { label: "Scheduled", value: "scheduled" },
];

const booleanOptions = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

const EditLiveEventModal = NiceModal.create(({ liveEvent, public_id }) => {
  const { selectedClient } = useContext(AppContext);
  const [newLiveEvent, setNewLiveEvent] = useState(public_id === null);
  const [savingLiveEvent, setSavingLiveEvent] = useState(false);
  const [localLiveEvent, setLiveEvent] = useState(liveEvent);
  const [localOpenDate, setLocalOpenDate] = useState("");
  const [localStartDate, setLocalStartDate] = useState("");
  const [inferenceWorkflowOptions, setInferenceWorkflowOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [localPreviewImage, setLocalPreviewImage] = useState(null);
  const [localPreviewVideo, setLocalPreviewVideo] = useState(null);

  const formRef = useRef(null);
  const liveEventRef = useRef(null);
  const modal = useModal();

  useEffect(() => {
    setLocalOpenDate(
      localLiveEvent?.open_date
        ? fromDateToString(getLocalDateFromString(localLiveEvent?.open_date))
        : ""
    );

    setLocalStartDate(
      localLiveEvent?.start_date
        ? fromDateToString(getLocalDateFromString(localLiveEvent?.start_date))
        : ""
    );

    console.log("!!! LOCAL LIVE EVENT: ", localLiveEvent);
  }, [localLiveEvent]);

  useEffect(() => {
    console.log("!!! USE EFFECT 1");

    if (liveEvent) {
      setLocalPreviewImage(liveEvent.image_url);
      setLocalPreviewVideo(liveEvent.video_url);
      setLiveEvent(liveEvent);
      return;
    }

    if (public_id) {
      getLiveEvent(public_id).then((liveEvent) => {
        console.log("!!! Live Event: ", liveEvent);
        setLocalPreviewImage(liveEvent.image_url);
        setLiveEvent(liveEvent);
      });
    } else {
      console.log("!!! NEW liveevent");
      setNewLiveEvent(true);
      setLiveEvent(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [public_id]);

  useEffect(() => {
    console.log("!!! FETCHING WORKFLOWS", selectedClient);
    const fetchInferenceWorkflows = async () => {
      const workflows = await getInferenceWorkflows();
      console.log("!!! WORKFLOWS: ", workflows);
      const options = workflows.map((workflow) => ({
        label: workflow.name,
        value: workflow.id,
      }));
      setInferenceWorkflowOptions(options);
    };
    fetchInferenceWorkflows();

    const fetchAgents = async () => {
      if (selectedClient) {
        const agents = await getAgents(selectedClient.id);
        console.log("!!! AGENTS: ", agents);
        const options = agents.map((agent) => ({
          label: `${agent.name}`,
          value: agent.id,
        }));
        setAgentOptions(options);
      }
    };
    fetchAgents();
  }, [selectedClient]);


  const onChooseImageFile = (e) => {
    const file = e.target.files[0];

    if (!file) {
      if (localPreviewImage) URL.revokeObjectURL(localPreviewImage);
      setLocalPreviewImage(null);
      setLiveEvent((prev) => ({
        ...prev,
        image_data: null,
        image_file_name: null,
      }));
      return;
    }


    if (localPreviewImage) {
      URL.revokeObjectURL(localPreviewImage);
    }

    const previewUrl = URL.createObjectURL(file);
    setLocalPreviewImage(previewUrl);

    const reader = new FileReader();

    reader.onloadend = () => {
      // reader.result is like "data:image/png;base64,AAAA..."
      const result = reader.result;
      const base64 =
        typeof result === "string"
          ? result.split(",")[1] // strip "data:*/*;base64,"
          : "";

      setLiveEvent((prev) => ({
        ...prev,
        image_data: base64,
        image_file_name: file.name,
      }));
    };

    reader.readAsDataURL(file);
  };

  const onChooseVideoFile = (e) => {
    const file = e.target.files[0];

    if (!file) {
      if (localPreviewVideo) URL.revokeObjectURL(localPreviewVideo);
      setLocalPreviewVideo(null);
      setLiveEvent((prev) => ({
        ...prev,
        video_data: null,
        video_file_name: null,
      }));
      return;
    }


    if (localPreviewVideo) {
      URL.revokeObjectURL(localPreviewVideo);
    }

    const previewUrl = URL.createObjectURL(file);
    setLocalPreviewVideo(previewUrl);

    const reader = new FileReader();

    reader.onloadend = () => {
      // reader.result is like "data:image/png;base64,AAAA..."
      const result = reader.result;
      const base64 =
        typeof result === "string"
          ? result.split(",")[1] // strip "data:*/*;base64,"
          : "";

      setLiveEvent((prev) => ({
        ...prev,
        video_data: base64,
        video_file_name: file.name,
      }));
    };

    reader.readAsDataURL(file);
  };

  const hide = (e) => {
    modal.remove();
  };

  const handleEventTypeChange = (eventType) => {
    const le = { ...localLiveEvent };
    le.event_type = eventType.value;
    setLiveEvent(le);
  };

  const handleAutoModerationChange = (autoModeration) => {
    const le = { ...localLiveEvent };
    le.auto_moderation = autoModeration.value === "true";
    console.log("!!! LE AUTO moderation: ", le.auto_moderation);
    setLiveEvent(le);
  };

  const handleChange = (e) => {
    console.log("!!! HANDLE CHANGE: ", e.target);
    const { name, value } = e.target;
    const le = { ...localLiveEvent };
    le[name] = value;
    console.log("!!! AFTER CHANGE: ", le);
    setLiveEvent(le);
  };

  const handleDateChange = (name, value) => {
    console.log("!!! HANDLE CHANGE: ", name, value);
    const le = { ...localLiveEvent };
    le[name] = value;
    console.log("!!! AFTER CHANGE: ", le);
    setLiveEvent(le);
  };

  const save = async (e) => {
    console.log("!!! SELECTED CLIENT: ", selectedClient);
    setSavingLiveEvent(true);

    const savedLiveEvent = { ...localLiveEvent };
    savedLiveEvent.client = selectedClient;
    savedLiveEvent.client.public_id = selectedClient.id;
    savedLiveEvent.timezone = getFullTimeZone();

    savedLiveEvent.open_date = localOpenDate;
    savedLiveEvent.start_date = localStartDate;
    console.log("!!! SAVE liveevent: ", savedLiveEvent);

    console.log("!!! TIMEZONE: ", savedLiveEvent.timezone);
    const updatedLiveEvent = await saveLiveEvent(savedLiveEvent);
    console.log("!!! SAVE UPDATED liveevent: ", updatedLiveEvent);
    modal.resolve(updatedLiveEvent);
    Swal.fire({
      title: "Success",
      text: "LiveEvent saved successfully",
      icon: "success",
      button: "OK",
      confirmButtonText: "OK",
    }).then((result) => {
      setSavingLiveEvent(false);
      modal.remove();
    });
  };

  return (
    <>
      <div className="w-5/12 h-auto force-height-90 force-center items-center flex overflow-x-hidden overflow-y-hidden overflow-hidden fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-full my-6 mx-auto max-w-3xl">
          {/*content*/}
          <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
            {/*header*/}
            <div className="flex items-start justify-between p-5 border-b border-solid border-blueGray-200 rounded-t">
              <h3 className="text-3xl font-semibold">
                {newLiveEvent ? "New Live Event" : "Edit Live Event"}
              </h3>
              <button
                className="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                onClick={hide}
              >
                <span className="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">
                  ×
                </span>
              </button>
            </div>
            {/*body*/}
            <div
              className="relative p-6 flex-auto overflow-y-auto"
              style={{ maxHeight: "70vh" }}
            >
              <div className="my-4 text-blueGray-500 text-lg leading-relaxed">
                <form ref={formRef} autoComplete="off">
                  <label>ID</label>
                  <Input
                    label="ID"
                    type="text"
                    placeholder="ID"
                    name="id"
                    value={localLiveEvent?.public_id ?? ""}
                    onChange={handleChange}
                    disabled={true}
                  />
                  <label>Title</label>
                  <Input
                    label="Title"
                    type="text"
                    placeholder="Title"
                    name="title"
                    value={localLiveEvent?.title ?? ""}
                    onChange={handleChange}
                  />
                  <label>Topic</label>
                  <Input
                    label="Topic"
                    type="text"
                    placeholder="Topic"
                    name="topic"
                    value={localLiveEvent?.topic ?? ""}
                    onChange={handleChange}
                  />
                  <label>Description</label>
                  <Input
                    label="Description"
                    type="textarea"
                    placeholder="Description"
                    name="description"
                    value={localLiveEvent?.description ?? ""}
                    onChange={handleChange}
                  />
                  <label>Supporting Documentation</label>
                  <Input
                    label="Supporting Documentation"
                    type="textarea"
                    placeholder="Supporting Documentation"
                    name="supporting_documentation"
                    value={localLiveEvent?.supporting_documentation ?? ""}
                    onChange={handleChange}
                  />
                  <label>Moderator Agent Instructions</label>
                  <Input
                    label="Moderator Agent Instructions"
                    type="textarea"
                    placeholder="Moderator Agent Instructions"
                    name="moderator_agent_instructions"
                    value={localLiveEvent?.moderator_agent_instructions ?? ""}
                    onChange={handleChange}
                  />
                  <label>Agent Name</label>
                  <Input
                    label="Agent Name"
                    type="text"
                    placeholder="Agent Name"
                    name="agent_name"
                    value={localLiveEvent?.agent_name ?? ""}
                    onChange={handleChange}
                  />
                  <label>Agent</label>
                  <Select
                    label="Agent"
                    placeholder="Agent"
                    name="agent_id"
                    value={
                      agentOptions.find(
                        (option) =>
                          option.value === (localLiveEvent?.agent_id ?? "")
                      ) ?? null
                    }
                    options={agentOptions}
                    onChange={(option) => {
                      const le = { ...localLiveEvent };
                      le.agent_id = option.value;
                      setLiveEvent(le);
                    }}
                  />
                  <label>Agent Workflow</label>
                  <Select
                    label="Agent Workflow"
                    placeholder="Agent Workflow"
                    name="agent_workflow"
                    value={
                      inferenceWorkflowOptions.find(
                        (option) =>
                          option.value ===
                          (localLiveEvent?.agent_workflow_id ?? "")
                      ) ?? null
                    }
                    options={inferenceWorkflowOptions}
                    onChange={(option) => {
                      const le = { ...localLiveEvent };
                      le.agent_workflow_id = option.value;
                      setLiveEvent(le);
                    }}
                  />
                  <label>
                    Relevance Threshold - The min score to display a question
                  </label>
                  <Input
                    label="Relevance Threshold"
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    placeholder="Relevance Threshold"
                    name="relevance_threshold"
                    value={localLiveEvent?.relevance_threshold ?? ""}
                    onChange={handleChange}
                  />
                  <label>
                    Auto Moderation
                    <br />
                    (Questions exceeding the threshold will be automatically
                    approved)
                    {localLiveEvent?.auto_moderation ?? "test"}
                  </label>
                  <Select
                    label="Auto Moderation"
                    placeholder="Auto Moderation"
                    name="auto_moderation"
                    value={
                      booleanOptions.find(
                        (option) =>
                          option.value === "true" &&
                          localLiveEvent?.auto_moderation
                      ) ?? { label: "No", value: "false" }
                    }
                    options={booleanOptions}
                    onChange={handleAutoModerationChange}
                  />
                  <label>
                    Open Time ({`${getAbbreviatedTimeZone()}`}) - When the event
                    is open for questions
                  </label>
                  <Input
                    label="Open Time"
                    type="datetime-local"
                    placeholder="Open Time"
                    name="open_date"
                    value={
                      localLiveEvent?.open_date
                        ? fromDateToString(
                          getLocalDateFromString(localLiveEvent?.open_date)
                        )
                        : ""
                    }
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      const utcString = newDate.toISOString().substr(0, 16);
                      setLocalOpenDate(e.target.value);
                      handleDateChange("open_date", utcString);
                    }}
                  />
                  <label>
                    Start Time ({`${getAbbreviatedTimeZone()}`}) - When the
                    event starts
                  </label>
                  <Input
                    label="Start Time"
                    type="datetime-local"
                    placeholder="Start Time"
                    name="start_date"
                    value={
                      localLiveEvent?.start_date
                        ? fromDateToString(
                          getLocalDateFromString(localLiveEvent.start_date)
                        )
                        : ""
                    }
                    onChange={(e) => {
                      const newDate = new Date(e.target.value);
                      const utcString = newDate.toISOString().substr(0, 16);
                      setLocalStartDate(e.target.value);
                      handleDateChange("start_date", utcString);
                    }}
                  />
                  <label>Event Type</label>
                  <Select
                    label="Event Type"
                    placeholder="Event Type"
                    name="event_type"
                    value={
                      eventTypeOptions.find(
                        (option) =>
                          option.value ===
                          (localLiveEvent?.event_type ?? "public")
                      ) ?? { label: "Public", value: "public" }
                    }
                    options={eventTypeOptions}
                    onChange={handleEventTypeChange}
                  />
                  <label>Price (USD)</label>
                  <Input
                    label="Price"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Price"
                    name="price"
                    value={localLiveEvent?.price ?? ""}
                    onChange={handleChange}
                  />
                  <label>Event Image</label>
                  {localPreviewImage ? (
                    <div style={{ marginBottom: "10px" }}>
                      <img
                        src={localPreviewImage}
                        alt="Event"
                        style={{ maxWidth: "100%", height: "auto" }}
                      />
                    </div>
                  ) : null}
                  <Input
                    type="file"
                    label="Event Image"
                    onChange={async (file) => {
                      console.log("!!! UPLOADED FILE: ", file);
                      onChooseImageFile(file);
                    }}
                  />

                  {localLiveEvent?.event_type === "scheduled" ? (
                    <>
                      <label>Event Video</label>
                      {localPreviewVideo ? (
                        <div style={{ marginBottom: "10px" }}>
                          <video
                            src={localPreviewVideo}
                            style={{ maxWidth: "100%", height: "auto" }}
                            controls
                          />
                        </div>
                      ) : null}
                      <Input
                        type="file"
                        label="Event Image"
                        onChange={async function(file) {
                          console.log("!!! UPLOADED FILE: ", file);
                          onChooseVideoFile(file);
                        }}
                      />
                    </>
                  ) : null}
                </form>
              </div>
            </div>
            <div className="relative p-6 flex-auto"></div>
            {/*footer*/}
            <div className="flex items-center justify-end p-6 border-t border-solid border-blueGray-200 rounded-b">
              {!savingLiveEvent ? (
                <button
                  className="text-red-500 background-transparent font-bold uppercase px-6 py-2 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                  type="button"
                  onClick={hide}
                >
                  Close
                </button>
              ) : null}
              <button
                className="bg-emerald-500 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                type="button"
                onClick={save}
                disabled={
                  savingLiveEvent ||
                  JSON.stringify(liveEvent) === liveEventRef.current
                }
                style={{
                  opacity:
                    JSON.stringify(liveEvent) === liveEventRef.current
                      ? 0.5
                      : 1,
                }}
              >
                {savingLiveEvent ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin"></i>{" "}
                    Processing...
                  </>
                ) : (
                  <>Save</>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
    </>
  );
});

NiceModal.register("edit-live-event", EditLiveEventModal);

const LiveEventsTable = ({ liveEvents = [], onUpdated = () => { } }) => {
  const activeUser = useSelector((state) => state.users.activeUser);
  const tableRef = useRef(null);
  const [localLiveEvents, setLocalLiveEvents] = useState(liveEvents);
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    console.log("!!! USE EFFECT 3: ", liveEvents);
    setLocalLiveEvents(liveEvents);
  }, [liveEvents]);

  useEffect(() => {
    console.log("!!! USE EFFECT 4");
    const interval = setInterval(() => {
      if (tableRef.current) {
        clearInterval(interval);
      } else {
        console.log("!!! TABLE REF IS NULL");
      }
    }, 10);
  }, []);

  window.formatDate = (date) => {
    const inputDate = new Date(date);

    const pad = (n) => n.toString().padStart(2, "0");

    const formattedDate =
      `${pad(inputDate.getDate())}-${pad(
        inputDate.getMonth() + 1
      )}-${inputDate.getFullYear()} ` +
      `${pad(inputDate.getHours())}:${pad(inputDate.getMinutes())}:${pad(
        inputDate.getSeconds()
      )} ` +
      `GMT${inputDate.toTimeString().slice(9, 14)} (${inputDate
        .toTimeString()
        .match(/\(([^)]+)\)/)[1]
        .split(" ")
        .map((w) => w[0])
        .join("")})`;
    return formattedDate;
  };

  window.edit = (public_id) => {
    window.location.href = `/live-event/edit/${public_id}`;
    return;
    /*
    NiceModal.show("edit-live-event", {
      public_id: public_id,
    }).then((updatedLiveEvent) => {
      console.log("!!! UPDATED liveevent 2: ", updatedLiveEvent);
      const newLocalLiveEvents = [...localLiveEvents];
      let foundLiveEvent = false;
      newLocalLiveEvents.forEach((item) => {
        if (item.public_id === updatedLiveEvent.public_id) {
          item.firstname = updatedLiveEvent.firstname;
          item.lastname = updatedLiveEvent.lastname;
          item.email = updatedLiveEvent.email;
          item.liveevent_group = updatedLiveEvent.liveevent_group;
          item.comped_subscription = updatedLiveEvent.comped_subscription;
          item.email_notifications_enabled =
            updatedLiveEvent.email_notifications_enabled;
          item.last_login = updatedLiveEvent.last_login;
          item.created_at = updatedLiveEvent.created_at;
          foundLiveEvent = true;
        }
      });

      if (!foundLiveEvent) {
        newLocalLiveEvents.push(updatedLiveEvent);
      }
      onUpdated(newLocalLiveEvents);
    });
        */
  };

  window.delete = (public_id) => {
    console.log("!!! liveevent DELETE: ", public_id);
    if (tableRef.current) {
      const table = tableRef.current.dt();
      const row = table.row((idx, data) => data[0] === public_id);

      if (row) {
        const data = row.data();
        Swal.fire({
          title: "Are you sure?",
          text: `You are trying to delete ${data[1]}. You won't be able to revert this!`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete the liveevent!",
        }).then(async (result) => {
          if (result.value) {
            setDeleting(true);
            const response = await deleteLiveEvent(public_id);
            console.log("!!! DELETE RESPONSE: ", response);
            const newLocalLiveEvents = [...localLiveEvents].filter(
              (liveevent) => liveevent.public_id !== public_id
            );

            onUpdated(newLocalLiveEvents);

            setDeleting(false);
            Swal.fire("Deleted!", "The liveevent has been deleted.", "success");
          }
        });
      }
    }
  };

  window.email = (public_id) => {
    NiceModal.show("email-liveevent", {
      public_id: public_id,
      dt: tableRef.current ? tableRef.current.dt() : null,
    });
  };

  window.showLiveEventDetails = (public_id) => {
    if (activeUser?.user_type.match(/client/)) {
      navigate(`/client/live-event/${public_id}#presenter`);
    } else {
      navigate(`/live-event/${public_id}#presenter`);
    }
  };

  window.moderateLiveEventDetails = (public_id) => {
    if (activeUser?.user_type.match(/client/)) {
      navigate(`/client/live-event/${public_id}#moderator`);
    } else {
      navigate(`/live-event/${public_id}#moderator`);
    }
  };

  const tableData = localLiveEvents.map((liveevent) => {
    return [
      liveevent.id,
      liveevent.public_id,
      `<div><div onclick="
document.querySelector('#copied-${liveevent.public_id}').style.display='block';
navigator.clipboard.writeText('https://www.streamy.ai/e/${liveevent.public_id}');">https://www.streamy.ai/e/${liveevent.public_id}</div><div style="display: none; font-weight: bold;" id="copied-${liveevent.public_id}">Copied!</div></div>`,
      liveevent.title,
      liveevent.description,
      liveevent.open_date
        ? dateTimeFormatter.format(getLocalDateFromString(liveevent.open_date))
        : "",
      liveevent.start_date
        ? dateTimeFormatter.format(getLocalDateFromString(liveevent.start_date))
        : "",
      liveevent.status,
      liveevent?.participant_count ?? 0,
      liveevent.started_at,
      liveevent.completed_at,
      `<div style="display: flex; flex-direction: row; width: 140px; flex-wrap: wrap;"><div style="display: flex; flex-direction: row;"><button style="width: 40px; display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 20px; color: black;" title="presenter" onClick="window.showLiveEventDetails('${liveevent.public_id}')" class="bg-orange-400 text-white active:bg-orange-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150" type="button">▶</button></div><div style="display: flex; flex-direction: row;"><button title="moderator" style="width: 40px; display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 20px; color: black;" onClick="window.moderateLiveEventDetails('${liveevent.public_id}')" class="bg-purple-400 text-white active:bg-purple-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150" type="button">👁</button></div><div style="display: flex; flex-direction: row;"><button title="edit" style="width: 40px; display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 20px; color: black;" onClick="window.edit('${liveevent.public_id}')" class="bg-emerald-400 text-white active:bg-emerald-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150" type="button">✎</button></div><div style="display: flex; flex-direction: row;"><button title="delete" style="width: 40px; display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 16px; color: black;" onClick="window.delete('${liveevent.public_id}')" class="bg-red-400 text-white active:bg-red-600 font-bold uppercase text-sm px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150" type="button">X</button></div>`,
    ];
  });

  return (
    <div>
      <DataTable
        ref={tableRef}
        data={tableData}
        className="display"
        options={{
          responsive: true,
          select: true,
          pageLength: 100,
          order: [[0, "desc"]],
          lengthChange: false,
          searching: false,
        }}
      >
        <thead style={{ color: "var(--main-font-color)", backgroundColor: "var(--content-item-bg)" }}>
          <tr>
            <th>ID</th>
            <th>Public ID</th>
            <th>Link</th>
            <th>Title</th>
            <th>Description</th>
            <th>Opening Time</th>
            <th>Starting Time</th>
            <th>Status</th>
            <th>Users</th>
            <th>Started At</th>
            <th>Completed At</th>
            <th>Actions</th>
          </tr>
        </thead>
      </DataTable>
      {deleting ? (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            backgroundColor: "white",
            padding: "20px",
            borderRadius: "10px",
            transform: "translate(-50%, -50%)",
            zIndex: 1000,
            alignItems: "center",
            justifyContent: "center",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #ccc",
          }}
        >
          <i
            className="fas fa-circle-notch fa-spin"
            style={{ fontSize: "50px", textAlign: "center" }}
          ></i>
          <div style={{ fontSize: "20px", fontWeight: "bold" }}>
            Deleting...
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default LiveEventsTable;
